import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Users, UserPlus, Fish, ExternalLink, Shield, UserCog } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { adminCreateUserSchema, AdminCreateUserFormData } from "@/lib/validations";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface UserRole {
  id: string;
  role: string;
}

interface UserProfile {
  id: string;
  angler_name: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  created_at: string;
  user_roles: UserRole[];
  catches?: {
    id: string;
    fish_species: string;
    weight_lbs: number;
    date_caught: string;
    status: string;
    photo_url: string | null;
  }[];
}

export const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const { toast } = useToast();

  const form = useForm<AdminCreateUserFormData>({
    resolver: zodResolver(adminCreateUserSchema),
    defaultValues: {
      email: "",
      password: "",
      angler_name: "",
    },
  });

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          *,
          user_roles (
            id,
            role
          ),
          catches (
            id,
            fish_species,
            weight_lbs,
            date_caught,
            status,
            photo_url
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (values: AdminCreateUserFormData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            angler_name: values.angler_name,
          },
        },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User created successfully",
      });

      setDialogOpen(false);
      form.reset();
      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    }
  };

  const handleAddRole = async () => {
    if (!selectedUser || !selectedRole) return;

    // Validate role is one of the allowed values
    const validRoles = ['admin', 'moderator', 'angler'];
    if (!validRoles.includes(selectedRole)) {
      toast({
        title: "Error",
        description: "Invalid role selected",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("user_roles")
        .insert([{ user_id: selectedUser.id, role: selectedRole }] as any);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedRole} role added to user`,
      });

      setRoleDialogOpen(false);
      setSelectedUser(null);
      setSelectedRole("");
      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add role",
        variant: "destructive",
      });
    }
  };

  const handleRemoveRole = async (userId: string, roleId: string) => {
    if (!confirm("Are you sure you want to remove this role?")) return;

    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", roleId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Role removed successfully",
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove role",
        variant: "destructive",
      });
    }
  };

  const openRoleDialog = (user: UserProfile) => {
    setSelectedUser(user);
    setRoleDialogOpen(true);
  };

  if (loading) {
    return <div className="text-center py-8">Loading users...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h2 className="text-2xl font-bold">All Users ({users.length})</h2>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreateUser)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="angler_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Angler Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full">Create User</Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Role to {selectedUser?.angler_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Select Role</label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="angler">Angler</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddRole} className="w-full" disabled={!selectedRole}>
                Add Role
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {users.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No users found
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {users.map((user) => (
            <Card key={user.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 mb-2">
                      {user.angler_name}
                    </CardTitle>
                    <CardDescription>
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openRoleDialog(user)}
                  >
                    <UserCog className="h-4 w-4 mr-2" />
                    Manage Roles
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {user.user_roles?.map((userRole) => (
                        <div key={userRole.id} className="flex items-center gap-1">
                          <Badge 
                            variant={userRole.role === 'admin' ? 'default' : 'secondary'}
                            className="flex items-center gap-1"
                          >
                            <Shield className="h-3 w-3" />
                            {userRole.role}
                            <button
                              onClick={() => handleRemoveRole(user.id, userRole.id)}
                              className="ml-1 hover:text-destructive"
                              aria-label={`Remove ${userRole.role} role`}
                            >
                              ×
                            </button>
                          </Badge>
                        </div>
                      ))}
                    </div>
                    {user.phone && (
                      <div className="text-sm">
                        <strong>Phone:</strong> {user.phone}
                      </div>
                    )}
                    {user.city && user.state && (
                      <div className="text-sm">
                        <strong>Location:</strong> {user.city}, {user.state}
                      </div>
                    )}
                  </div>

                  {user.catches && user.catches.length > 0 && (
                    <Collapsible 
                      open={expandedUsers.has(user.id)}
                      onOpenChange={() => toggleUserExpanded(user.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="w-full justify-between">
                          <div className="flex items-center gap-2">
                            <Fish className="h-4 w-4" />
                            <span>Catches ({user.catches.length})</span>
                          </div>
                          <span className="text-xs">
                            {expandedUsers.has(user.id) ? '▼' : '▶'}
                          </span>
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-2 mt-2">
                        {user.catches
                          .sort((a, b) => new Date(b.date_caught).getTime() - new Date(a.date_caught).getTime())
                          .slice(0, 5)
                          .map((catch_) => (
                            <Card
                              key={catch_.id}
                              className="p-3 hover:bg-accent cursor-pointer transition-colors"
                              onClick={() => navigate(`/catch/${catch_.id}`)}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold truncate">{catch_.fish_species}</p>
                                    <Badge 
                                      variant={
                                        catch_.status === 'verified' ? 'default' :
                                        catch_.status === 'rejected' ? 'destructive' :
                                        'secondary'
                                      }
                                      className="text-xs"
                                    >
                                      {catch_.status}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {catch_.weight_lbs} lbs • {new Date(catch_.date_caught).toLocaleDateString()}
                                  </p>
                                </div>
                                {catch_.photo_url && (
                                  <img 
                                    src={catch_.photo_url} 
                                    alt={catch_.fish_species}
                                    className="w-12 h-12 rounded object-cover"
                                  />
                                )}
                                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              </div>
                            </Card>
                          ))}
                        {user.catches.length > 5 && (
                          <p className="text-xs text-muted-foreground text-center pt-2">
                            Showing 5 of {user.catches.length} catches
                          </p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
